import { useState } from 'react';

interface Referral {
  id: string;
  username: string;
  emoji: string;
  joinedAt: string;
  status: 'active' | 'pending' | 'spawned';
  agentsSpawned: number;
}

const MOCK_REFERRALS: Referral[] = [
  { id: 'r1', username: 'mike_hvac', emoji: '🔧', joinedAt: '2 days ago', status: 'active', agentsSpawned: 3 },
  { id: 'r2', username: 'lisa_fintech', emoji: '💹', joinedAt: '5 days ago', status: 'spawned', agentsSpawned: 1 },
  { id: 'r3', username: 'dave_ops', emoji: '⚙️', joinedAt: '1 week ago', status: 'active', agentsSpawned: 5 },
  { id: 'r4', username: 'pending_user1', emoji: '⏳', joinedAt: 'Invited yesterday', status: 'pending', agentsSpawned: 0 },
  { id: 'r5', username: 'pending_user2', emoji: '⏳', joinedAt: 'Invited 3 days ago', status: 'pending', agentsSpawned: 0 },
];

const REWARDS_TIERS = [
  { referrals: 1, reward: 'Referral Rookie Badge', emoji: '🌱', credits: 50, unlocked: true },
  { referrals: 5, reward: 'Networker Badge', emoji: '🔗', credits: 250, unlocked: false },
  { referrals: 10, reward: 'Recruiter Badge + Premium Theme', emoji: '🎖️', credits: 500, unlocked: false },
  { referrals: 25, reward: 'Ambassador Badge + Custom CSS', emoji: '👑', credits: 1500, unlocked: false },
  { referrals: 50, reward: 'Legend Badge + 1 Month Premium', emoji: '🏆', credits: 5000, unlocked: false },
];

export function ReferralsPage() {
  const [copied, setCopied] = useState(false);
  const referralCode = 'SYM-JDV-2026';
  const referralLink = `https://SYMSVOX.com/join?ref=${referralCode}`;

  const activeReferrals = MOCK_REFERRALS.filter(r => r.status !== 'pending').length;
  const totalCreditsEarned = activeReferrals * 50;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      {/* Header + Stats */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Invite & Earn</div>
        <div className="p-3">
          <div className="text-center mb-3">
            <div className="text-2xl mb-1">🎁</div>
            <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
              Invite friends. Earn credits. Unlock badges.
            </div>
            <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              You both get 50 credits when they spawn their first agent.
            </div>
          </div>

          {/* Referral Code */}
          <div className="p-2 mb-2" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-separator)' }}>
            <div className="text-2xs font-bold mb-1" style={{ color: 'var(--text-muted)' }}>YOUR REFERRAL CODE</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-bold text-sm font-mono" style={{ color: '#ffd700' }}>{referralCode}</code>
              <button
                onClick={() => handleCopy(referralCode)}
                className="px-2 py-1 text-2xs font-bold cursor-pointer"
                style={{ background: 'var(--accent)', color: 'white', border: '1px solid var(--accent-hover)' }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Referral Link */}
          <div className="p-2 mb-3" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-separator)' }}>
            <div className="text-2xs font-bold mb-1" style={{ color: 'var(--text-muted)' }}>REFERRAL LINK</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-2xs truncate" style={{ color: 'var(--text-secondary)' }}>{referralLink}</code>
              <button
                onClick={() => handleCopy(referralLink)}
                className="px-2 py-1 text-2xs font-bold cursor-pointer shrink-0"
                style={{ background: 'var(--accent)', color: 'white', border: '1px solid var(--accent-hover)' }}
              >
                Copy Link
              </button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join me on SYMSVOX! Use code ${referralCode} for 50 free credits. ${referralLink}`)}`)}
              className="flex-1 py-1.5 text-xs font-bold cursor-pointer"
              style={{ background: '#1da1f2', color: 'white', border: '1px solid #1a8cd8' }}
            >
              Share on X
            </button>
            <button
              onClick={() => window.open(`https://discord.com`)}
              className="flex-1 py-1.5 text-xs font-bold cursor-pointer"
              style={{ background: '#5865f2', color: 'white', border: '1px solid #4752c4' }}
            >
              Share on Discord
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 px-3 py-2" style={{ borderTop: '1px solid var(--border-separator)', background: 'var(--bg-deep)' }}>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{MOCK_REFERRALS.length}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Invited</div>
          </div>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: '#10b981' }}>{activeReferrals}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Joined</div>
          </div>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: '#ffd700' }}>{totalCreditsEarned}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Credits Earned</div>
          </div>
        </div>
      </div>

      {/* Reward Tiers */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Reward Milestones</div>
        <div className="p-2">
          {REWARDS_TIERS.map((tier, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-2 py-2"
              style={{
                borderBottom: i < REWARDS_TIERS.length - 1 ? '1px solid var(--border-separator)' : 'none',
                opacity: tier.unlocked ? 1 : 0.6,
              }}
            >
              <span className="text-lg">{tier.emoji}</span>
              <div className="flex-1">
                <div className="font-bold text-xs" style={{ color: tier.unlocked ? '#ffd700' : 'var(--text-primary)' }}>
                  {tier.referrals} Referrals — {tier.reward}
                </div>
                <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>+{tier.credits} credits</div>
              </div>
              <span className="text-xs" style={{ color: tier.unlocked ? '#10b981' : 'var(--text-muted)' }}>
                {tier.unlocked ? '✅' : '🔒'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Referral List */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Your Referrals</div>
        <div>
          {MOCK_REFERRALS.map((ref, i) => (
            <div
              key={ref.id}
              className="flex items-center gap-2 px-3 py-2"
              style={{ borderBottom: i < MOCK_REFERRALS.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
            >
              <span className="text-lg">{ref.emoji}</span>
              <div className="flex-1">
                <div className="font-bold text-xs" style={{ color: 'var(--text-link)' }}>{ref.username}</div>
                <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>{ref.joinedAt}</div>
              </div>
              <span
                className="px-1.5 py-0.5 text-2xs font-bold"
                style={{
                  background: ref.status === 'active' ? '#10b98120' : ref.status === 'spawned' ? '#3b82f620' : '#6b728020',
                  color: ref.status === 'active' ? '#10b981' : ref.status === 'spawned' ? '#3b82f6' : '#6b7280',
                  border: `1px solid ${ref.status === 'active' ? '#10b98140' : ref.status === 'spawned' ? '#3b82f640' : '#6b728040'}`,
                }}
              >
                {ref.status === 'active' ? `${ref.agentsSpawned} agents` : ref.status === 'spawned' ? `${ref.agentsSpawned} agent` : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
